import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { default as widgetsEventBus } from '@meitu/xdesign-widgets/dist/utils/eventBus';
import IconTextItem from '../icon-text-item/icon-text-item';
import styles from './dropdown.module.less';

export interface DropdownRef {
    setShowToolMenu: (show: boolean) => void;
}

interface DropdownProps {
    icon?: string;
    weight?: number;
    active?: boolean;
    text?: string;
    children: React.ReactNode;
    style?: React.CSSProperties;
    trigger?: 'click' | 'hover';
    showArrow?: boolean;
    position?: 'top' | 'bottom';
    outerStyle?: React.CSSProperties;
    fontSize?: number;
    clickCallback?: (show: boolean) => void;
}

const Dropdown = forwardRef<DropdownRef, DropdownProps>(
    (
        {
            icon,
            text,
            children,
            active,
            style,
            showArrow = true,
            trigger = 'hover',
            position = 'bottom',
            weight,
            outerStyle,
            fontSize,
            clickCallback,
        },
        ref,
    ) => {
        const [showToolMenu, setShowToolMenu] = useState(false);
        const timer = useRef<NodeJS.Timeout | null>(null);

        useImperativeHandle(ref, () => ({
            setShowToolMenu: (show: boolean) => {
                setShowToolMenu(show);
            },
        }));
        const onToolMouseEnter = () => {
            if (trigger === 'click') {
                return;
            }
            if (timer.current) {
                clearTimeout(timer.current);
                timer.current = null;
            }
            // 启动300毫秒定时器，执行展开
            timer.current = setTimeout(() => {
                setShowToolMenu(true);
            }, 300);
        };
        const onToolMouseLeave = () => {
            if (trigger === 'click') {
                return;
            }
            if (timer.current) {
                clearTimeout(timer.current);
                timer.current = null;
            }
            setShowToolMenu(false);
        };

        const onToolClick = () => {
            if (trigger === 'hover') {
                clickCallback?.(true);
                return;
            }
            clickCallback?.(!showToolMenu);
            setShowToolMenu(!showToolMenu);
        };

        useEffect(() => {
            return () => {
                if (timer.current) {
                    clearTimeout(timer.current);
                }
            };
        }, []);

        useEffect(() => {
            const handler = () => {
                setShowToolMenu(false);
            };
            widgetsEventBus.on('close-dropdown', handler);
            return () => {
                widgetsEventBus.off('close-dropdown', handler);
            };
        }, []);

        return (
            <div
                className={styles.dropdown}
                onClick={onToolClick}
                onMouseEnter={onToolMouseEnter}
                onMouseLeave={onToolMouseLeave}
                style={outerStyle}
            >
                <IconTextItem
                    icon={icon}
                    text={text}
                    showArrow={showArrow}
                    active={active}
                    weight={weight}
                    fontSize={fontSize}
                />

                <div
                    className={`${styles.dropdownContent} ${showToolMenu ? styles.show : ''} ${position === 'top' ? styles.top : styles.bottom}`}
                    style={style}
                >
                    <div className={styles.innerContent}>{children}</div>
                </div>
            </div>
        );
    },
);

Dropdown.displayName = 'Dropdown';

export default Dropdown;
